import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslations } from "hooks";
import { getTranslation } from "common";

interface ReleaseNotesSplashProps {
  open: boolean;
  releaseNote: import("model/ReleaseNote").ReleaseNote | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
}

const mdContentSx = {
  "& h1": { fontSize: "1.5rem", fontWeight: 600, mt: 2, mb: 1 },
  "& h2": { fontSize: "1.25rem", fontWeight: 600, mt: 2, mb: 1 },
  "& h3": { fontSize: "1.1rem", fontWeight: 600, mt: 1.5, mb: 0.5 },
  "& p": { mb: 1, lineHeight: 1.55 },
  "& ul, & ol": { pl: 3, mb: 1 },
  "& li": { mb: 0.5 },
  "& code": {
    fontFamily: "monospace",
    fontSize: "0.9em",
    bgcolor: "action.hover",
    px: 0.5,
    borderRadius: 0.5,
  },
  "& pre": {
    bgcolor: "action.hover",
    p: 1.5,
    borderRadius: 1,
    overflowX: "auto",
    "& code": { bgcolor: "transparent", p: 0 },
  },
  "& a": { color: "primary.main", textDecoration: "underline" },
  "& blockquote": {
    borderLeft: 3,
    borderColor: "divider",
    pl: 2,
    color: "text.secondary",
    m: 0,
    mb: 1,
  },
  "& table": { borderCollapse: "collapse", width: "100%", mb: 1 },
  "& th, & td": {
    border: 1,
    borderColor: "divider",
    p: 1,
    textAlign: "left",
  },
  "& hr": { border: 0, borderTop: 1, borderColor: "divider", my: 2 },
} as const;

const ReleaseNotesSplash: React.FC<ReleaseNotesSplashProps> = ({
  open,
  releaseNote,
  loading,
  error,
  onClose,
}) => {
  const translations = useTranslations();
  const isReady = Boolean(releaseNote?.content);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="release-notes-dialog"
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" component="span">
          {getTranslation(translations, "releasenotes.title")}
        </Typography>
        {releaseNote && (
          <Chip
            size="small"
            color="primary"
            label={`v${releaseNote.version}`}
            data-testid="release-notes-version-chip"
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Typography color="error" data-testid="release-notes-error">
            {error}
          </Typography>
        ) : !isReady ? (
          <Stack alignItems="center" justifyContent="center" minHeight={120} spacing={1}>
            <CircularProgress size={28} data-testid="release-notes-loading" />
            <Typography variant="body2" color="text.secondary">
              {getTranslation(translations, "releasenotes.loading")}
            </Typography>
          </Stack>
        ) : (
          <Box sx={mdContentSx} data-testid="release-notes-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {releaseNote!.content}
            </ReactMarkdown>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          variant="contained"
          data-testid="release-notes-dismiss"
          disabled={loading}
        >
          {getTranslation(translations, "releasenotes.dismiss")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseNotesSplash;